import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { trpc } from 'src/trpc';
import { useToast } from 'src/components/useToast';
import {
  ORDER_LIFECYCLE_STAGES,
  OrderStatusEnum,
  getOrderLifecycleStageIndex,
} from '@libs/schemas/src/order';
import { StatusDisplay, getStatusDisplayLabel } from 'src/components/StatusDisplay';
import { LuTriangleAlert } from 'react-icons/lu';

interface Props {
  orderId: string;
  currentStatus: string;
  open: boolean;
  onClose: () => void;
}

// Natural status order following the typical workflow from VALID_STATUS_TRANSITIONS
const NATURAL_STATUS_ORDER: OrderStatusEnum[] = [
  // Normal workflow progression
  OrderStatusEnum.Created,
  OrderStatusEnum.DropOffScheduled,
  OrderStatusEnum.PendingDropOff,
  OrderStatusEnum.InTransitDropOff,
  OrderStatusEnum.DropOffConfirmed,
  OrderStatusEnum.PickupRequested,
  OrderStatusEnum.PickupScheduled,
  OrderStatusEnum.PendingPickup,
  OrderStatusEnum.InTransitPickup,
  OrderStatusEnum.PickupConfirmed,
  OrderStatusEnum.WasteProcessing,
  // Completion states
  OrderStatusEnum.Completed,
  OrderStatusEnum.Refunded,
  // Payment states
  OrderStatusEnum.PaymentPending,
  OrderStatusEnum.PaymentFailed,
  OrderStatusEnum.RefundFailed,
  // Special states
  OrderStatusEnum.Restricted,
  OrderStatusEnum.Cancelled,
];

/**
 * Helper text shown permanently under `completed`, which the backend allows from every
 * status (see Rule 2 of validateManualOrderStatusTransition). It replaces the skip
 * explanation the option used to carry, so the exemption reads as a stated rule rather
 * than as a missing warning.
 */
const COMPLETED_HELPER_TEXT = 'Can be selected from any status.';

/**
 * Mirrors the backend's forward-skip guardrail (see validateManualOrderStatusTransition)
 * so blocked statuses are visibly unselectable instead of failing on submit.
 *
 * This covers only what the client can decide from `currentStatus` alone. Two rules are
 * deliberately left to the backend and surface in the inline error banner:
 *   - the required-operational-data rule, which needs deliveryConfirmedAt/pickupConfirmedAt
 *   - re-entry from an exception status (restricted, cancelled, payment-*), which is
 *     anchored to the furthest stage recorded in the order's statusHistory
 * The backend is the source of truth; this is a hint, never an authorization.
 */
const getSkipReason = (currentStatus: string, target: OrderStatusEnum): string | undefined => {
  // `completed` is exempt from the skip rule on the backend, so it must never be
  // presented as blocked. Keeping the exemption here rather than at the call site
  // means every consumer of getSkipReason agrees with the server.
  if (target === OrderStatusEnum.Completed) {
    return undefined;
  }

  const currentIndex = getOrderLifecycleStageIndex(currentStatus as OrderStatusEnum);
  const targetIndex = getOrderLifecycleStageIndex(target);

  if (currentIndex === -1 || targetIndex === -1 || targetIndex <= currentIndex + 1) {
    return undefined;
  }

  const skipped = ORDER_LIFECYCLE_STAGES.slice(currentIndex + 1, targetIndex)
    .map((stage) => stage.join(' or '))
    .join(', ');

  return `Would skip ${skipped}`;
};

/**
 * Whether completing from `currentStatus` leaves intervening lifecycle stages
 * uncompleted, which is what the confirmation step needs to spell out.
 *
 * A status outside the linear lifecycle (cancelled, refunded, restricted, payment-*)
 * counts as skipping: the client cannot tell from `currentStatus` alone how far the
 * order actually got, so it states the explicit version rather than implying the
 * normal flow was followed.
 */
const completionSkipsStages = (currentStatus: string): boolean => {
  const currentIndex = getOrderLifecycleStageIndex(currentStatus as OrderStatusEnum);
  const completedIndex = getOrderLifecycleStageIndex(OrderStatusEnum.Completed);

  return currentIndex === -1 || completedIndex > currentIndex + 1;
};

export const ManualStatusChangeDialog: FC<Props> = ({ orderId, currentStatus, open, onClose }) => {
  const [selectedStatus, setSelectedStatus] = useState<OrderStatusEnum | undefined>();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const utils = trpc.useUtils();
  const { errorToast, successToast } = useToast();

  const confirmationHeadingRef = useRef<HTMLHeadingElement>(null);
  const proceedButtonRef = useRef<HTMLButtonElement>(null);
  // Set when leaving the selection step so Back can put focus where it was.
  const restoreFocusToProceed = useRef(false);
  const submittingRef = useRef(false);

  const skipReasons = useMemo(
    () =>
      new Map(NATURAL_STATUS_ORDER.map((status) => [status, getSkipReason(currentStatus, status)])),
    [currentStatus],
  );

  const isCompletingWithSkippedStages =
    selectedStatus === OrderStatusEnum.Completed && completionSkipsStages(currentStatus);

  // The two steps share one drawer, so the step change is not announced on its own.
  // Moving focus to the heading is what tells a screen reader the content swapped,
  // and returning it to Proceed keeps the selection step navigable after Back.
  useEffect(() => {
    if (showConfirmation) {
      confirmationHeadingRef.current?.focus();
      return;
    }

    if (restoreFocusToProceed.current) {
      restoreFocusToProceed.current = false;
      proceedButtonRef.current?.focus();
    }
  }, [showConfirmation]);

  const { mutate, isPending } = trpc.admin.order.adminManuallySetOrderStatus.useMutation({
    onSuccess: () => {
      submittingRef.current = false;
      utils.admin.order.adminGetOrderRelatedInfoById.invalidate(orderId);
      successToast({
        title: 'Status Updated',
        description: 'Order status has been manually updated without sending notifications.',
      });
      handleClose();
    },
    onError: (error) => {
      submittingRef.current = false;
      // Keep the backend's specific reason on screen — the toast alone disappears
      // before an admin can act on it.
      setErrorMessage(error.message);
      errorToast({
        title: 'Failed to Update Status',
        description: error.message,
      });
    },
  });

  const handleClose = () => {
    setSelectedStatus(undefined);
    setShowConfirmation(false);
    setErrorMessage(undefined);
    // Closing hands focus back to the trigger via the drawer, so the Back
    // restoration must not fire on the next open.
    restoreFocusToProceed.current = false;
    onClose();
  };

  const handleProceed = () => {
    if (!selectedStatus) return;
    setErrorMessage(undefined);
    restoreFocusToProceed.current = true;
    setShowConfirmation(true);
  };

  const handleConfirm = () => {
    // The ref closes the gap before the mutation hook re-renders as pending.
    if (!selectedStatus || isPending || submittingRef.current) return;
    submittingRef.current = true;
    setErrorMessage(undefined);
    mutate({
      orderId,
      status: selectedStatus,
    });
  };

  const handleBack = () => {
    setShowConfirmation(false);
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          handleClose();
        }
      }}
    >
      <SheetContent
        className="sm:max-w-[500px] h-full p-0"
        closeButtonClassName={
          showConfirmation
            ? 'right-2 top-2 flex h-11 w-11 shrink-0 items-center justify-center focus:ring-inset focus:ring-offset-0'
            : undefined
        }
      >
        <div className="grid h-full grid-rows-[auto_1fr_auto]">
          {!showConfirmation ? (
            <>
              <SheetHeader className="border-b bg-white px-6 py-4">
                <SheetTitle>Manual Status Change</SheetTitle>
                <SheetDescription>
                  Select a new status for this order. This will bypass validation and notifications.
                </SheetDescription>
              </SheetHeader>

              <div className="overflow-y-auto px-6 py-6">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="text-base font-medium">Current Status</label>
                    <StatusDisplay status={currentStatus as OrderStatusEnum} className="text-sm" />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="text-base font-medium">Select New Status</label>
                    <RadioGroup
                      value={selectedStatus}
                      onValueChange={(value) => setSelectedStatus(value as OrderStatusEnum)}
                      className="flex flex-col gap-2"
                    >
                      {NATURAL_STATUS_ORDER.map((status) => {
                        const isCurrentStatus = status === currentStatus;
                        const skipReason = skipReasons.get(status);
                        const isDisabled = isCurrentStatus || Boolean(skipReason);
                        // `completed` carries standing helper text instead of a skip
                        // explanation, because it is selectable from everywhere.
                        const helperText =
                          status === OrderStatusEnum.Completed && !isCurrentStatus
                            ? COMPLETED_HELPER_TEXT
                            : undefined;
                        const descriptionId =
                          skipReason || helperText ? `${status}-description` : undefined;
                        return (
                          <Label
                            htmlFor={status}
                            key={status}
                            className={`flex min-h-[44px] items-center space-x-3 rounded-md border p-3 ${
                              isDisabled
                                ? 'bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed'
                                : 'bg-white border-gray-200 hover:bg-gray-50 cursor-pointer'
                            } ${selectedStatus === status ? 'border-primary ring-2 ring-primary ring-opacity-20' : ''}`}
                          >
                            <RadioGroupItem
                              value={status}
                              id={status}
                              disabled={isDisabled}
                              aria-describedby={descriptionId}
                            />
                            <span
                              className={`flex min-h-[44px] flex-1 min-w-0 flex-col justify-center ${
                                isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                              }`}
                            >
                              <StatusDisplay status={status} className="text-sm" />
                              {skipReason && (
                                <span
                                  id={descriptionId}
                                  className="mt-1 block break-words text-xs text-red-700"
                                >
                                  {skipReason}. Advance one stage at a time.
                                </span>
                              )}
                              {helperText && (
                                <span
                                  id={descriptionId}
                                  className="mt-1 block break-words text-xs text-muted-foreground"
                                >
                                  {helperText}
                                </span>
                              )}
                            </span>
                          </Label>
                        );
                      })}
                    </RadioGroup>
                  </div>
                </div>
              </div>

              <SheetFooter className="border-t bg-white px-6 py-4">
                <div className="flex justify-end gap-3 w-full">
                  <Button variant="outline" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button
                    ref={proceedButtonRef}
                    onClick={handleProceed}
                    disabled={!selectedStatus || isPending}
                  >
                    Proceed
                  </Button>
                </div>
              </SheetFooter>
            </>
          ) : (
            <>
              {/* The local close target ends 52px from the edge; 64px padding leaves 12px clear. */}
              <SheetHeader className="border-b bg-white py-4 pl-6 pr-16">
                <SheetTitle ref={confirmationHeadingRef} tabIndex={-1}>
                  {isCompletingWithSkippedStages
                    ? 'Mark order as completed?'
                    : 'Confirm Status Change'}
                </SheetTitle>
                <SheetDescription>
                  {isCompletingWithSkippedStages ? (
                    <>
                      This changes the status from{' '}
                      {getStatusDisplayLabel(currentStatus as OrderStatusEnum)} to Completed without
                      completing the intervening stages.
                    </>
                  ) : (
                    <>
                      Please confirm this manual status change. This action will be logged in the
                      order history.
                    </>
                  )}
                </SheetDescription>
              </SheetHeader>

              <div className="overflow-y-auto px-6 py-6">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-muted-foreground">From</span>
                      <StatusDisplay
                        status={currentStatus as OrderStatusEnum}
                        className="text-sm"
                      />
                    </div>
                    <div className="text-xl text-muted-foreground">→</div>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-muted-foreground">To</span>
                      <StatusDisplay status={selectedStatus!} className="text-sm" />
                    </div>
                  </div>

                  <div className="flex gap-2 items-start p-3 rounded-lg bg-red-50 border border-red-200">
                    <LuTriangleAlert className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex flex-col gap-1">
                      <p className="text-sm font-medium text-red-900">Important</p>
                      <ul className="text-sm text-red-800 list-disc list-inside space-y-1">
                        <li>No email notification will be sent</li>
                        <li>No SMS notification will be sent</li>
                        <li>Normal workflow validation will be bypassed</li>
                        <li>Other forward changes must follow the normal stage order.</li>
                        <li>This change will be logged with your user ID</li>
                      </ul>
                    </div>
                  </div>

                  {errorMessage && (
                    <div className="flex gap-2 items-start p-3 rounded-lg bg-red-100 border border-red-300">
                      <LuTriangleAlert className="w-5 h-5 text-red-700 mt-0.5 flex-shrink-0" />
                      <div className="flex flex-col gap-1">
                        <p className="text-sm font-medium text-red-900">Status change blocked</p>
                        <p className="text-sm text-red-800">{errorMessage}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <SheetFooter className="border-t bg-white px-6 py-4">
                <div className="flex justify-end gap-3 w-full">
                  <Button variant="outline" onClick={handleBack} disabled={isPending}>
                    Back
                  </Button>
                  <Button variant="destructive" onClick={handleConfirm} disabled={isPending}>
                    {isPending
                      ? 'Updating...'
                      : isCompletingWithSkippedStages
                        ? 'Mark as completed'
                        : 'Confirm Change'}
                  </Button>
                </div>
              </SheetFooter>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
